import React from 'react';
import { StepDirection, StepTransition, T } from '../../../motion';
import { SuccessTick } from '../../button/app-button';
import {
  Content,
  LockedReason,
  Marker,
  PhoneLine,
  Root,
  Segment,
  SegmentFill,
  Segments,
  StepButton,
  StepItem,
  StepLabel,
  StepList,
  StepLook,
  StepStatic,
} from './gated-stepper.styled';
import { countedSteps, resolveStep, StepDef } from './resolve-step';

export type { StepDef, StepState } from './resolve-step';

/**
 * A FLOW WITH GATES — the guest check-in's step frame (plan S6). Not the
 * Stepper in components/stepper, which is a minus / value / plus counter.
 *
 * THE PRIMITIVE NEVER DECIDES A STATE (R-S6.3). The caller passes each step's
 * state as the server sent it; this only draws it:
 *   - done          a button (review it), ticked;
 *   - current       the server's next step; marked aria-current when it is the
 *                   one on screen, a button back to it when a done step is
 *                   being reviewed instead;
 *   - locked        text, never a button, aria-disabled, with its reason shown;
 *   - not_required  not rendered and not counted ("Step x of y" skips it).
 * Which step is on screen is the caller's activeId (use resolveStep for a
 * deep link). Only an activeId that is not a counted step at all (unknown, or
 * not_required) is drawn where resolveStep would land it, so the bar and the
 * list never point at nothing; the content is always keyed by activeId.
 *
 * Phone first: below sm the list is one line, "Step 2 of 4 · Identity"; the
 * segment bar above it is a progressbar at every width. The active step's
 * content swaps inside StepTransition, keyed by activeId, in `direction`.
 */

export interface GatedStepperProps {
  steps: ReadonlyArray<StepDef>;
  activeId: string;
  onSelect: (id: string) => void;
  /** The progressbar's accessible name, e.g. "Check-in progress". */
  ariaLabel: string;
  /** The active step's content. */
  children?: React.ReactNode;
  direction?: StepDirection;
  /** "Step 2 of 4" in the guest's language. English when omitted. */
  stepOf?: (position: number, total: number) => string;
  className?: string;
}

const englishStepOf = (position: number, total: number): string =>
  `Step ${position} of ${total}`;

const lookOf = (step: StepDef, activeId: string): StepLook => {
  if (step.id === activeId) return 'active';
  if (step.state === 'done') return 'done';
  if (step.state === 'locked') return 'locked';

  return 'open';
};

export const GatedStepper: React.FC<GatedStepperProps> = ({
  steps,
  activeId,
  onSelect,
  ariaLabel,
  children,
  direction = 'forward',
  stepOf = englishStepOf,
  className,
}) => {
  const counted = countedSteps(steps);
  const shownId = counted.some(step => step.id === activeId)
    ? activeId
    : resolveStep(activeId, steps);
  const activeIndex = counted.findIndex(step => step.id === shownId);
  const total = counted.length;
  const position = activeIndex + 1;
  const active = activeIndex >= 0 ? counted[activeIndex] : undefined;
  const where = active ? stepOf(position, total) : '';

  return (
    <Root className={className}>
      <Segments
        role="progressbar"
        aria-label={ariaLabel}
        aria-valuemin={1}
        aria-valuemax={total}
        aria-valuenow={position}
        aria-valuetext={active ? `${where}: ${active.label}` : undefined}
      >
        {counted.map((step, index) => (
          <Segment key={step.id}>
            <SegmentFill
              $current={index === activeIndex}
              initial={false}
              animate={{
                scaleX: step.state === 'done' || index <= activeIndex ? 1 : 0,
              }}
              transition={T.base}
            />
          </Segment>
        ))}
      </Segments>

      {active ? (
        <PhoneLine data-gated-stepper="phone-line">
          {`${where} · ${active.label}`}
        </PhoneLine>
      ) : null}

      <StepList data-gated-stepper="list">
        {counted.map((step, index) => {
          const look = lookOf(step, shownId);
          const marker = (
            <Marker $look={look} aria-hidden="true">
              {look === 'done' ? <SuccessTick /> : index + 1}
            </Marker>
          );
          const label = <StepLabel $look={look}>{step.label}</StepLabel>;

          if (look === 'done' || look === 'open') {
            return (
              <StepItem key={step.id}>
                <StepButton
                  type="button"
                  data-step-id={step.id}
                  onClick={() => onSelect(step.id)}
                >
                  {marker}
                  {label}
                </StepButton>
              </StepItem>
            );
          }

          return (
            <StepItem key={step.id}>
              <StepStatic
                $look={look}
                data-step-id={step.id}
                aria-current={look === 'active' ? 'step' : undefined}
                aria-disabled={look === 'locked' ? 'true' : undefined}
              >
                {marker}
                {label}
              </StepStatic>
              {look === 'locked' && step.lockedReason ? (
                <LockedReason>{step.lockedReason}</LockedReason>
              ) : null}
            </StepItem>
          );
        })}
      </StepList>

      <Content>
        <StepTransition stepKey={activeId} direction={direction}>
          {children}
        </StepTransition>
      </Content>
    </Root>
  );
};
